import React, { createContext, Dispatch, FunctionComponent, ReactNode, useContext, useEffect, useReducer } from 'react';

import axios, { AxiosResponse } from 'axios';

import { Route } from 'core/util/Route';
import { route } from 'core/util/Routing';

export interface Config {
    urls: { [keyof: string]: string };
    secrets: { [keyof: string]: Secret };
    credential2TenantId: number | undefined;
}

export interface Secret {
    agencyID: number;
    version: string;
    key: string;
    value: string | boolean;
}

export interface ConfigContainer {
    config?: Config;
    loading: boolean;
    error?: object;
}

enum ConfigActionType {
    FetchStart,
    FetchSuccess,
    FetchError,
}

export interface ConfigAction {
    type: ConfigActionType;
    config?: Config;
    error?: object;
}

interface ConfigProviderProps {
    children: ReactNode;
}

const ConfigStateContext = createContext<ConfigContainer | undefined>(undefined);
const ConfigDispatchContext = createContext<Dispatch<ConfigAction> | undefined>(undefined);

const configReducer = (configContainer: ConfigContainer, action: ConfigAction): ConfigContainer => {
    switch (action.type) {
        case ConfigActionType.FetchStart:
            return { loading: true };

        case ConfigActionType.FetchSuccess:
            return { loading: false, config: action.config };

        case ConfigActionType.FetchError:
            return { loading: false, error: action.error };

        default:
            throw new TypeError(`unrecognized action type ${action.type}`);
    }
};

export const ConfigProvider: FunctionComponent<ConfigProviderProps> = ({ children }) => {
    const [state, dispatch] = useReducer(configReducer, { loading: true });

    return (
        <ConfigStateContext.Provider value={state}>
            <ConfigDispatchContext.Provider value={dispatch}>{children}</ConfigDispatchContext.Provider>
        </ConfigStateContext.Provider>
    );
};

const useConfigState = (): ConfigContainer => {
    const context = useContext(ConfigStateContext);

    if (context === undefined) {
        throw new Error('useConfigState must be used within a ConfigProvider');
    }

    return context;
};

const useConfigDispatch = (): Dispatch<ConfigAction> => {
    const context = useContext(ConfigDispatchContext);

    if (context === undefined) {
        throw new Error('useConfigDispatch must be used within a ConfigProvider');
    }

    return context;
};

export const useConfig = (): [ConfigContainer, Dispatch<ConfigAction>] => {
    const [configContainer, dispatch] = [useConfigState(), useConfigDispatch()];

    useEffect(() => {
        // Fetch config
        if (!configContainer.config) {
            (async (): Promise<any> => {
                try {
                    dispatch({ type: ConfigActionType.FetchStart });

                    const response: AxiosResponse = await axios.get('/config');

                    dispatch({ type: ConfigActionType.FetchSuccess, config: response.data });
                } catch (error) {
                    dispatch({ type: ConfigActionType.FetchError, error });
                    window.location.href = route(
                        `${Route.Auth}?path=${encodeURIComponent(window.location.pathname + window.location.search)}`,
                    );
                }
            })();
        }
    }, [configContainer.config, dispatch]);

    return [configContainer, dispatch];
};
